// ── Polls ───────────────────────────────────────────────────────────────────────
// Admins ask, members answer. Results are visible to everyone in the group — the thing that stays
// hidden on an anonymous poll is *who* voted, which the server enforces by not sending voters at all.
import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  GROUP_POLLS_QUERY, CREATE_POLL_MUTATION, VOTE_POLL_MUTATION,
  CLOSE_POLL_MUTATION, DELETE_POLL_MUTATION,
} from '../graphql';

export default function PollsTab({ groupId, isGroupAdmin, isMember }) {
  const [showCreate, setShowCreate] = useState(false);
  const { data, loading } = useQuery(GROUP_POLLS_QUERY, {
    variables: { groupId }, fetchPolicy: 'cache-and-network', skip: !isMember && !isGroupAdmin,
  });
  const polls = data?.groupPolls || [];

  if (!isMember && !isGroupAdmin) {
    return (
      <div className="empty-state" style={{ padding: '30px 0' }}>
        <div className="empty-state-icon">📊</div>
        <div className="empty-state-title">Members only</div>
        <div className="empty-state-desc">Join the group to see and answer its polls.</div>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
      {isGroupAdmin && (
        <button className="btn btn-primary btn-sm" style={{ marginBottom: 16 }} onClick={() => setShowCreate(s => !s)}>
          {showCreate ? '✕ Cancel' : '+ New poll'}
        </button>
      )}

      {showCreate && <CreatePoll groupId={groupId} onDone={() => setShowCreate(false)} />}

      {loading && !polls.length && <div className="loading-center"><div className="spinner" /></div>}

      {!loading && !polls.length && !showCreate && (
        <div className="empty-state" style={{ padding: '30px 0' }}>
          <div className="empty-state-icon">📊</div>
          <div className="empty-state-title">No polls yet</div>
          <div className="empty-state-desc">
            {isGroupAdmin ? 'Ask the group something — a date, a dish, a theme.' : 'The organiser hasn’t asked anything yet.'}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {polls.map(p => <PollCard key={p.id} poll={p} groupId={groupId} isGroupAdmin={isGroupAdmin} isMember={isMember} />)}
      </div>
    </div>
  );
}

function CreatePoll({ groupId, onDone }) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [error, setError] = useState('');
  const [createPoll, { loading }] = useMutation(CREATE_POLL_MUTATION, {
    refetchQueries: ['GroupPolls'], onCompleted: onDone,
    onError: e => setError(e.message),
  });

  const setOpt = (i, v) => setOptions(o => o.map((x, ix) => ix === i ? v : x));
  const filled = options.map(o => o.trim()).filter(Boolean);
  const canSubmit = question.trim() && filled.length >= 2;

  return (
    <div className="card" style={{ background: 'var(--surface2)', marginBottom: 16 }}>
      <div className="form-group">
        <label className="form-label">Question *</label>
        <input className="input" placeholder="e.g. Which day suits the rehearsal?" maxLength={300}
          value={question} onChange={e => setQuestion(e.target.value)} />
      </div>

      <div className="form-group">
        <label className="form-label">Options * (at least two)</label>
        {options.map((o, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input className="input" placeholder={`Option ${i + 1}`} maxLength={120}
              value={o} onChange={e => setOpt(i, e.target.value)} />
            {options.length > 2 && (
              <button type="button" className="btn btn-ghost btn-sm"
                onClick={() => setOptions(opts => opts.filter((_, ix) => ix !== i))}>✕</button>
            )}
          </div>
        ))}
        {options.length < 10 && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOptions(o => [...o, ''])}>+ Add option</button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--font-sm)', cursor: 'pointer' }}>
          <input type="checkbox" checked={allowMultiple} onChange={e => setAllowMultiple(e.target.checked)} />
          Allow several answers
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--font-sm)', cursor: 'pointer' }}>
          <input type="checkbox" checked={isAnonymous} onChange={e => setIsAnonymous(e.target.checked)} />
          Anonymous
        </label>
      </div>
      <p style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 12 }}>
        {isAnonymous
          ? 'Counts are visible to everyone; who voted for what is visible to nobody, including you.'
          : 'Everyone in the group will see who voted for each option.'}
      </p>

      {error && <div style={{ padding: '8px 12px', background: 'rgba(220,38,38,0.08)', color: 'var(--danger)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-sm)', marginBottom: 10 }}>{error}</div>}

      <button className="btn btn-primary" style={{ width: '100%' }} disabled={loading || !canSubmit}
        onClick={() => { setError(''); createPoll({ variables: { groupId, question: question.trim(), options: filled, allowMultiple, isAnonymous } }); }}>
        {loading ? 'Creating…' : '📊 Ask the group'}
      </button>
    </div>
  );
}

function PollCard({ poll, isGroupAdmin, isMember }) {
  const [pending, setPending] = useState(null);   // indexes chosen but not yet sent
  const [votePoll, { loading: voting }] = useMutation(VOTE_POLL_MUTATION, { refetchQueries: ['GroupPolls'] });
  const [closePoll] = useMutation(CLOSE_POLL_MUTATION, { refetchQueries: ['GroupPolls'] });
  const [deletePoll] = useMutation(DELETE_POLL_MUTATION, { refetchQueries: ['GroupPolls'] });

  const chosen = pending ?? poll.myVotes ?? [];
  const hasVoted = (poll.myVotes || []).length > 0;
  const canVote = isMember && !poll.isClosed;
  // Hide the tally until they've answered, so early results can't steer the vote.
  const showResults = hasVoted || poll.isClosed || isGroupAdmin;

  const toggle = (i) => {
    if (!canVote) return;
    if (poll.allowMultiple) setPending(chosen.includes(i) ? chosen.filter(x => x !== i) : [...chosen, i]);
    else setPending([i]);
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', marginBottom: 4 }}>
        <div style={{ fontWeight: 800, fontSize: 'var(--font-base)' }}>📊 {poll.question}</div>
        {poll.isClosed && <span className="badge badge-dim" style={{ fontSize: 11, flexShrink: 0 }}>Closed</span>}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
        {poll.creatorName ? `Asked by ${poll.creatorName} · ` : ''}
        {poll.voterCount} {poll.voterCount === 1 ? 'person has' : 'people have'} answered
        {poll.allowMultiple && ' · pick as many as you like'}
        {poll.isAnonymous ? ' · anonymous' : ' · names visible'}
      </div>

      <div style={{ display: 'grid', gap: 6 }}>
        {poll.options.map(o => {
          const picked = chosen.includes(o.index);
          return (
            <button key={o.index} type="button" onClick={() => toggle(o.index)} disabled={!canVote}
              style={{
                position: 'relative', textAlign: 'left', padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                border: `2px solid ${picked ? 'var(--primary)' : 'var(--border)'}`,
                background: 'var(--surface2)', cursor: canVote ? 'pointer' : 'default',
                overflow: 'hidden', fontFamily: 'inherit',
              }}>
              {/* Result bar sits behind the label so the option stays readable either way. */}
              {showResults && (
                <div style={{ position: 'absolute', inset: 0, width: `${o.percent}%`, background: 'var(--primary-light)', transition: 'width 0.3s' }} />
              )}
              <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                <span style={{ fontWeight: picked ? 700 : 500, fontSize: 'var(--font-sm)' }}>
                  {picked ? '● ' : '○ '}{o.text}
                </span>
                {showResults && (
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', flexShrink: 0 }}>
                    {o.votes} · {o.percent}%
                  </span>
                )}
              </div>
              {showResults && !poll.isAnonymous && o.voters.length > 0 && (
                <div style={{ position: 'relative', fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>
                  {o.voters.map(v => v.userName).join(', ')}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {canVote && (
        <button className="btn btn-primary btn-sm" style={{ marginTop: 10, width: '100%' }}
          disabled={voting || !chosen.length}
          onClick={async () => {
            try { await votePoll({ variables: { pollId: poll.id, optionIndexes: chosen } }); setPending(null); }
            catch (e) { alert(e.message); }
          }}>
          {voting ? 'Saving…' : hasVoted ? 'Change my answer' : 'Submit answer'}
        </button>
      )}
      {!isMember && <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 8 }}>Join the group to answer.</p>}

      {isGroupAdmin && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {!poll.isClosed && (
            <button className="btn btn-ghost btn-sm" onClick={() => closePoll({ variables: { pollId: poll.id } }).catch(e => alert(e.message))}>
              🔒 Close voting
            </button>
          )}
          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }}
            onClick={() => { if (window.confirm('Delete this poll and all its answers?')) deletePoll({ variables: { pollId: poll.id } }).catch(e => alert(e.message)); }}>
            🗑️ Delete
          </button>
        </div>
      )}
    </div>
  );
}
